import { configureStore } from '@reduxjs/toolkit';
import usersReducer, { UsersState } from '../users/usersReducer';
import authReducer, { AuthState } from '../auth/authReducer';
import alertReducer, { AlertsState } from '../alerts/alertReducer';
import filteredAlertReducer, { FilteredAlertsState } from '../alerts/filters/filteredAlertsReducer';
import feedbackReducer, { FeedbackState } from '../feedback/feedbackReducer';
import amlReducer, { AmlState } from '../aml/amlReducer';
import schemaReducer, { SchemaState } from '../schema/schemaReducer';
import { rootReducer } from './rootReducer';
import { combineReducersWithRoot } from './combineReducersWithRoot';
import tablesReducer, { TablesState } from '../table/redux/tablesReducer';
import scenariosReducer, { ScenariosState } from '../scenario/scenarioReducer';
import dashboardsReducer, { DashboardsState } from '../dashboard/dashboardsReducer';
import screeningAlertsReducer, { ScreeningAlertsState } from '../screening/screeningReducer';
import organisationReducer, { OrganisationState } from '../organisation/organisationReducer';
import filtersReducer, { FiltersState } from '../alerts/filters/filtersReducer';
import screeningFilterReducer, { ScreeningFilterState } from '../screening/screeningFiltersReducer';
import quickStatusCommentsReducer, { QuickStatusCommentsState } from '../screening/alert/status/statusCommentsReducer';
import amlFiltersReducer, { AmlFiltersState } from '../aml/amlFiltersReducer';
import dataUploadReducer, { DataUploadState } from '../data-upload/dataUploadReducer';
import riskReducer, { RiskState } from '../risk/riskReducer';
import screeningAlertsStatusesReducer, {
  ScreeningAlertStatusesState,
} from '../screening/alert-status/alertStatusesReducer';
import customListReducer, { CustomListState } from '../custom-lists/customListReducer';
import alertFiltersReducer, { AlertFiltersState } from '../alerts/filters/alertFiltersReducer';
import confirmationModalReducer, { ConfirmationModalState } from '../confirmation-modal/confirmationModalReducer';
import customListsReducer, { CustomListsState } from '../custom-lists/customListsReducer';

export interface AppState {
  auth: AuthState;
  users: UsersState;
  organisation: OrganisationState;
  alerts: AlertsState;
  alertFilters: AlertFiltersState;
  filteredAlerts: FilteredAlertsState;
  feedback: FeedbackState;
  aml: AmlState;
  amlFilters: AmlFiltersState;
  schema: SchemaState;
  tables: TablesState;
  scenarios: ScenariosState;
  dashboards: DashboardsState;
  screeningAlerts: ScreeningAlertsState;
  screeningAlertStatuses: ScreeningAlertStatusesState;
  filters: FiltersState;
  screeningFilters: ScreeningFilterState;
  statusComments: QuickStatusCommentsState;
  dataUpload: DataUploadState;
  risk: RiskState;
  customList: CustomListState;
  customLists: CustomListsState;
  confirmationModal: ConfirmationModalState;
}

const childReducers = {
  auth: authReducer,
  users: usersReducer,
  organisation: organisationReducer,
  alerts: alertReducer,
  alertFilters: alertFiltersReducer,
  filteredAlerts: filteredAlertReducer,
  feedback: feedbackReducer,
  aml: amlReducer,
  amlFilters: amlFiltersReducer,
  schema: schemaReducer,
  tables: tablesReducer,
  scenarios: scenariosReducer,
  dashboards: dashboardsReducer,
  screeningAlerts: screeningAlertsReducer,
  screeningAlertStatuses: screeningAlertsStatusesReducer,
  filters: filtersReducer,
  screeningFilters: screeningFilterReducer,
  statusComments: quickStatusCommentsReducer,
  dataUpload: dataUploadReducer,
  risk: riskReducer,
  customList: customListReducer,
  customLists: customListsReducer,
  confirmationModal: confirmationModalReducer,
};

export const setupStore = (preloadedState?: any) =>
  configureStore({
    reducer: combineReducersWithRoot(rootReducer, childReducers),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
    preloadedState: preloadedState,
  });

const store = setupStore();

export type AppDispatch = typeof store.dispatch;

export default store;
